import type { Messages } from '../catalogue.en'

/**
 * The Person, in Russian.
 *
 * One id, one translation, one word on every screen — the rule the English
 * module states and the Hebrew one was extracted to satisfy. Russian is where
 * it bites hardest: `email`, `почта` and `электронная почта` are all ordinary
 * Russian for one field, so the surface picks one and the picker stays here.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const persons: Partial<Messages> = {
  'person.identity': 'Личные данные',
  'person.contact': 'Контакты',

  'person.fullName': 'Полное имя',
  'person.phone': 'Телефон',
  'person.email': 'Электронная почта',

  'person.notRecorded': 'Не указано',

  'person.edit': 'Изменить данные',
  'person.save': 'Сохранить данные',
  'person.saving': 'Сохранение…',
  'person.cancel': 'Отмена',
  'person.lastUpdated': 'Последнее обновление',

  'person.discardTitle': 'Несохранённые изменения',
  'person.discardDetail': 'Внесённые здесь изменения будут отменены. Уже сохранённые данные не затрагиваются.',
  'person.discardChanges': 'Отменить изменения',
  'person.keepEditing': 'Продолжить редактирование',

  'person.staleRevision': 'Эти данные изменились в другом месте, пока вы редактировали. Ваш текст сохранён; загрузите сохранённую редакцию, чтобы увидеть текущие данные перед сохранением.',
  'person.reloadSaved': 'Загрузить сохранённую редакцию',
  'person.reloadFailed': 'Не удалось загрузить сохранённую редакцию. Ваши изменения на месте; попробуйте ещё раз.',
  'person.saveRejected': 'Данные отклонены в том виде, как они введены. Проверьте поля и попробуйте ещё раз.',
  'person.saveFailed': 'Не удалось сохранить данные. Попробуйте ещё раз.',

  'person.names.title': 'Имена',
  'person.names.edit': 'Изменить имена',
  'person.names.saveNames': 'Сохранить имена',
  'person.names.loading': 'Загрузка имён…',
  'person.names.loadFailed': 'Не удалось загрузить имена.',
  'person.names.notFound': 'Для этого человека имена не записаны.',
  'person.names.unsavedIntent': 'изменения имён',

  'person.names.kind.family': 'Фамилия',
  'person.names.kind.given': 'Имя',
  'person.names.kind.patronymic': 'Отчество',
  'person.names.kind.prefix': 'Титул перед именем',
  'person.names.kind.suffix': 'Дополнение после имени',

  'person.names.use': 'Назначение',
  'person.names.use.unknown': 'Не указано',
  'person.names.use.usual': 'Обычное',
  'person.names.use.official': 'Официальное',
  'person.names.use.temp': 'Временное',
  'person.names.use.nickname': 'Прозвище',
  'person.names.use.anonymous': 'Анонимное',
  'person.names.use.old': 'Прежнее',
  'person.names.use.maiden': 'Девичья фамилия',

  'person.names.source': 'Источник',
  'person.names.source.reported': 'Со слов',
  'person.names.source.document': 'По документу',
  'person.names.source.passport': 'По паспорту',
  'person.names.source.migration': 'Перенесённая запись',
  'person.names.sourceReference': 'Реквизиты документа',

  'person.names.order': 'Порядок',
  'person.names.order.givenFamily': 'Сначала имя',
  'person.names.order.familyGiven': 'Сначала фамилия',
  'person.names.shortStyle': 'Краткая форма',
  'person.names.shortStyle.full': 'Как полное',
  'person.names.shortStyle.initials': 'Инициалы с фамилией',
  'person.names.shortStyle.firstGiven': 'Первое имя с фамилией',

  'person.names.language': 'Язык имени',
  'person.names.language.unknown': 'Не указан',
  'person.names.international': 'Международное написание',
  'person.names.preferred': 'Предпочтительное',
  'person.names.periodStart': 'Используется с',
  'person.names.periodEnd': 'Используется по',

  'person.names.primaryMark': 'Отображается',
  'person.names.preferredMark': 'Предпочтительное',
  'person.names.internationalMark': 'Международное',
  'person.names.enteredInErrorMark': 'Введено по ошибке',
  'person.names.periodFrom': 'С {start}',
  'person.names.periodUntil': 'По {end}',
  'person.names.periodBetween': 'С {start} по {end}',

  'person.names.nth': 'Имя {n}',
  'person.names.parts': 'Части имени',
  'person.names.partKind': 'Вид части {n}',
  'person.names.addPart': 'Добавить часть',
  'person.names.movePartUp': 'Переместить часть {n} выше',
  'person.names.movePartDown': 'Переместить часть {n} ниже',
  'person.names.removePart': 'Удалить часть {n}',
  'person.names.addName': 'Добавить имя',
  'person.names.remove': 'Удалить это имя',
  'person.names.endUse': 'Завершить использование',
  'person.names.enteredInError': 'Введено по ошибке',
  'person.names.previewFull': 'Полное:',
  'person.names.previewShort': 'Краткое:',
  'person.names.previewPending': 'Предпросмотр появится по мере ввода.',
  'person.names.shownAs': 'Отображается как',
  'person.names.shownWith': 'рядом с',

  'person.names.problem.none': 'Нужно хотя бы одно имя.',
  'person.names.problem.blankPart': 'Каждой части имени нужно значение. Удалите часть вместо того, чтобы оставлять её пустой.',
  'person.names.problem.familyTwice': 'У имени может быть только одна фамилия.',
  'person.names.problem.periodOrder': 'Последний день использования не может быть раньше первого.',
  'person.names.problem.migrationSource': 'Это имя перенесено из прежней системы. После изменения укажите, откуда взято написание.',

  'person.names.saveFailed': 'Не удалось сохранить имена. Попробуйте ещё раз.',
  'person.names.invalid': 'Имена отклонены в том виде, как они введены. Проверьте поля и попробуйте ещё раз.',
  'person.names.ambiguous': 'Два из этих имён отображались бы в одном месте. Отметьте одно как предпочтительное или завершите использование другого.',
  'person.names.inactive': 'Эта запись неактивна, поэтому её имена нельзя изменить.',
  'person.names.staleRevision': 'Имена изменились в другом месте, пока вы редактировали. Ваш текст сохранён; загрузите сохранённую редакцию перед сохранением.',

  'person.names.reasonLegend': 'Причина',
  'person.names.reasonOtherDetail': 'Укажите причину',
  'person.names.reasonHint': 'Выберите ближайшую причину или напишите свою.',
  'person.names.reasonOwed': 'Чтобы сохранить, укажите, почему меняются имена.',
  'person.names.reason.correction': 'Исправление ошибки',
  'person.names.reason.nameChange': 'Имя изменилось',
  'person.names.reason.documentUpdate': 'Обновлён документ',
  'person.names.reason.preferenceChange': 'Изменилось предпочтение',
  'person.names.reason.endUse': 'Имя больше не используется',
  'person.names.reason.enteredInError': 'Имя введено по ошибке',
  'person.names.reason.other': 'Другое',
  'person.names.event.registration': 'Регистрация',
  'person.names.event.migration': 'Перенос',

  'person.history': 'История',
  'person.names.historyLabel': 'История: имена',
  'person.names.historyLoading': 'Загрузка истории…',
  'person.names.historyFailed': 'Не удалось загрузить историю.',
  'person.names.historyEmpty': 'Предыдущих редакций нет.',
  'person.names.historyMore': 'Предыдущие редакции',
  'person.names.recordedAt': 'Записано',
  'person.names.actorSystem': 'Система',
  'person.change': 'Изменить',
  'person.noChanges': 'Пока ничего не изменено.',
  'person.names.formerly': 'ранее',
  'person.names.group.print': 'Как имя печатается',
  'person.names.group.meaning': 'Для чего это имя',
  'person.names.group.sourcePeriod': 'Источник и период',
  "person.names.advanced": "Дополнительно",
  "person.names.optionalReason": "Причина изменения",
  "person.names.optional": "Необязательно",
  "person.names.addPatronymic": "Добавить отчество",
  "person.names.addMiddle": "Добавить второе имя",
  "person.names.national": "Написание на национальном языке",
  "person.names.addOther": "Другой вариант (дополнительно)…",
  "person.names.keepOne": "Последнее написание нельзя удалить — исправьте или замените его.",
  "person.names.problem.internationalScript": "Для международного написания используйте латиницу.",
  'person.names.updated': 'Имена обновлены',
  "person.names.noReason": "Не указывать причину",
}
